'use client';
import React, { useState } from 'react';
import { Download, CheckCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import StatusBadge from '@/components/ui/StatusBadge';
import { Invoice } from './PaymentsBillingContent';

export default function InvoiceModal({ invoice: inv, onClose }: { invoice: Invoice; onClose: () => void }) {
  const [marking, setMarking] = useState(false);

  const handleMarkPaid = async () => {
    setMarking(true);
    // TODO: BACKEND — PATCH /api/invoices/{inv.id}/mark-paid
    await new Promise((r) => setTimeout(r, 1000));
    setMarking(false);
    toast.success(`Invoice ${inv.invoiceNumber} marked as paid`);
    onClose();
  };

  return (
    <Modal open onClose={onClose} title={`Invoice ${inv.invoiceNumber}`} subtitle={`Issued ${inv.issuedDate}`} size="md">
      <div className="space-y-5">
        {/* Municipality Header */}
        <div className="flex items-start justify-between p-4 bg-secondary rounded-lg">
          <div>
            <p className="text-sm font-700 text-primary">Nyandeni Local Municipality</p>
            <p className="text-xs text-muted-foreground">Cemetery Services Department</p>
            <p className="text-xs text-muted-foreground mt-1">VAT Reg No: 4680123456</p>
          </div>
          <StatusBadge status={inv.status} />
        </div>

        {/* Billing Details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Billed To</p>
            <p className="text-sm font-600 text-foreground">{inv.accountHolder}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Cemetery</p>
            <p className="text-sm font-600 text-foreground">{inv.cemetery}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Grave ID</p>
            <p className="text-sm font-mono-data font-600">{inv.graveId}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Due Date</p>
            <p className={`text-sm font-600 ${inv.status === 'overdue' ? 'text-negative' : 'text-foreground'}`}>
              {inv.dueDate}
            </p>
          </div>
        </div>

        {/* Line Items */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-muted/30 border-b border-border grid grid-cols-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">
            <span>Description</span>
            <span className="text-center">Fee Type</span>
            <span className="text-right">Amount</span>
          </div>
          <div className="px-4 py-3 grid grid-cols-3 items-center">
            <span className="text-sm text-foreground">{inv.description}</span>
            <span className="text-xs text-center text-muted-foreground">{inv.feeType}</span>
            <span className="text-sm font-700 tabular-nums text-right">R {inv.amount.toLocaleString('en-ZA')}</span>
          </div>
          <div className="px-4 py-2 bg-muted/20 border-t border-border flex justify-between">
            <span className="text-xs text-muted-foreground">VAT (0% — Municipal Exemption)</span>
            <span className="text-xs text-muted-foreground">R 0</span>
          </div>
          <div className="px-4 py-2.5 bg-secondary border-t border-primary/20 flex justify-between">
            <span className="text-sm font-700 text-primary">Total Due</span>
            <span className="text-sm font-700 tabular-nums text-primary">R {inv.amount.toLocaleString('en-ZA')}</span>
          </div>
        </div>

        {inv.paidDate !== '—' && (
          <div className="flex items-center gap-2 p-3 bg-[#f0fdf4] rounded-lg border border-positive/20">
            <CheckCircle size={14} className="text-positive" />
            <span className="text-xs font-600 text-positive">Payment received on {inv.paidDate}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          {inv.status !== 'paid' && (
            <button onClick={handleMarkPaid} disabled={marking} className="btn-primary text-sm flex-1 justify-center">
              {marking ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <><CheckCircle size={14} /> Mark as Paid</>
              )}
            </button>
          )}
          <button onClick={() => toast.success('Invoice downloaded')} className="btn-secondary text-sm">
            <Download size={14} /> Download
          </button>
          <button onClick={() => toast.success('Sent to printer')} className="btn-ghost text-sm">
            <Printer size={14} /> Print
          </button>
        </div>
      </div>
    </Modal>
  );
}